/**
 * Files on a ticket — the list, and the ways to add one.
 *
 * WHY THIS IS ONE COMPONENT FOR BOTH SIDES
 * The technician view and the customer portal show the same files under the
 * same rules; the only differences are which axios client carries the session,
 * which URL prefix the endpoints sit behind, and whether delete is offered. Two
 * near-identical components would drift, and the half that drifted would be the
 * one nobody looked at.
 *
 * THREE WAYS TO ADD A FILE, ON PURPOSE
 * Picker, drag-and-drop, and PASTE. Paste is the one that matters: the single
 * most common attachment on a helpdesk ticket is a screenshot, and a screenshot
 * lives on the clipboard. Making someone save it to Desktop first, find it in a
 * file dialog, and then delete it later is three steps of friction on the most
 * frequent action there is.
 *
 * DOWNLOADS GO THROUGH THE API CLIENT, NOT AN <a href>
 * The session is a bearer token in a header, not a cookie, so a plain link
 * would arrive unauthenticated and 401. Each file is fetched as a blob and
 * handed to a temporary object URL.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { AxiosInstance } from "axios";
import { formatDateTime } from "../utils/datetime";

export type TicketAttachment = {
  id: number;
  ticket_id: number;
  comment_id: number | null;
  filename: string;
  content_type: string | null;
  size_bytes: number;
  is_internal: boolean;
  source: string;
  uploaded_by_email: string | null;
  created_at: string;
  inline_safe: boolean;
};

type Props = {
  /** Authenticated axios instance — the staff client or the portal client. */
  client: AxiosInstance;
  /** Endpoint prefix, e.g. "/api/v1/tickets/12" or "/api/v1/portal/tickets/12". */
  basePath: string;
  /** Staff may remove a file; a customer may not remove one we sent them. */
  canDelete?: boolean;
  /** Hidden entirely on a closed ticket, where uploading would go unread. */
  canUpload?: boolean;
  /**
   * Attach to a specific reply. Staff pass the id of a comment they just
   * posted so the file lands in context rather than loose on the ticket.
   */
  commentId?: number | null;
};

const MAX_MB = 25;

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A glyph per family. Enough to tell a log from a screenshot at a glance. */
function iconFor(a: TicketAttachment): string {
  const t = a.content_type || "";
  if (t.startsWith("image/")) return "🖼";
  if (t === "application/pdf") return "📕";
  if (t.startsWith("text/") || t === "application/json" || t === "application/xml") return "📄";
  if (t.includes("zip") || t.includes("compressed")) return "🗜";
  if (t.startsWith("video/")) return "🎬";
  if (t.includes("word")) return "📘";
  if (t.includes("sheet") || t.includes("excel")) return "📗";
  return "📎";
}

const SOURCE_LABEL: Record<string, string> = {
  email: "from email",
  portal: "from the customer",
  staff: "",
};

export default function TicketAttachments({
  client,
  basePath,
  canDelete = false,
  canUpload = true,
  commentId = null,
}: Props) {
  const [items, setItems] = useState<TicketAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  /** Object URLs for image previews, keyed by attachment id. */
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const fileInput = useRef<HTMLInputElement>(null);
  const zoneRef = useRef<HTMLDivElement>(null);
  /** Revoked on unmount — an object URL pins its blob in memory until then. */
  const createdUrls = useRef<string[]>([]);

  const load = useCallback(async () => {
    try {
      const r = await client.get(`${basePath}/attachments`);
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [client, basePath]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      createdUrls.current.forEach((u) => URL.revokeObjectURL(u));
    },
    [],
  );

  /**
   * Thumbnails. Fetched individually because each needs the auth header, and
   * only for types the server has declared safe to render — the flag comes from
   * the server precisely so the browser is never asked to render something the
   * uploader chose.
   */
  useEffect(() => {
    let cancelled = false;
    items
      .filter((a) => a.inline_safe && !previews[a.id])
      .forEach(async (a) => {
        try {
          const r = await client.get(`${basePath}/attachments/${a.id}/download`, {
            responseType: "blob",
          });
          if (cancelled) return;
          const url = URL.createObjectURL(r.data);
          createdUrls.current.push(url);
          setPreviews((p) => ({ ...p, [a.id]: url }));
        } catch {
          /* a missing thumbnail is not worth an error message */
        }
      });
    return () => {
      cancelled = true;
    };
    // previews is deliberately not a dependency: adding one would re-run this
    // on every thumbnail that loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, client, basePath]);

  const upload = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    setError("");
    setBusy(true);
    try {
      for (const f of list) {
        // Checked here as well as on the server, so an obviously oversized file
        // fails immediately instead of after uploading 25 MB to be refused.
        if (f.size > MAX_MB * 1024 * 1024) {
          setError(`${f.name} is larger than the ${MAX_MB} MB limit.`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        if (commentId != null) fd.append("comment_id", String(commentId));
        try {
          await client.post(`${basePath}/attachments`, fd);
        } catch (e: any) {
          setError(e?.response?.data?.detail || `Could not upload ${f.name}.`);
        }
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  const download = async (a: TicketAttachment) => {
    try {
      const r = await client.get(`${basePath}/attachments/${a.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
      // Revoked on a timer, not immediately: the click is handled after this
      // function returns.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "That file could not be downloaded.");
    }
  };

  const remove = async (a: TicketAttachment) => {
    if (!window.confirm(`Delete ${a.filename}? This cannot be undone.`)) return;
    try {
      await client.delete(`${basePath}/attachments/${a.id}`);
      setItems((xs) => xs.filter((x) => x.id !== a.id));
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not delete that file.");
    }
  };

  /**
   * Paste anywhere in the drop zone. Scoped to the zone rather than the whole
   * document so that pasting text into the reply box does not also try to
   * upload whatever else is on the clipboard.
   */
  const onPaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) {
      e.preventDefault();
      upload(files);
    }
  };

  if (loading) {
    return <div className="text-xs text-gray-400 dark:text-gray-500">Loading attachments…</div>;
  }

  return (
    <div>
      {items.length > 0 && (
        <ul className="space-y-2 mb-3">
          {items.map((a) => (
            <li
              key={a.id}
              className={`group flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
                a.is_internal
                  ? "border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-500/5"
                  : "border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/40 hover:bg-gray-100/70 dark:hover:bg-gray-700/40"
              }`}
            >
              {previews[a.id] ? (
                <img
                  src={previews[a.id]}
                  alt={a.filename}
                  className="h-10 w-10 rounded object-cover border border-gray-200 dark:border-gray-600 shrink-0"
                />
              ) : (
                <span className="h-10 w-10 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-lg shrink-0">
                  {iconFor(a)}
                </span>
              )}

              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => download(a)}
                  className="block truncate text-sm font-medium text-blue-700 dark:text-blue-400 hover:underline text-left"
                  title={a.filename}
                >
                  {a.filename}
                </button>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5 flex-wrap">
                  <span>{humanSize(a.size_bytes)}</span>
                  <span>·</span>
                  <span>{formatDateTime(a.created_at)}</span>
                  {SOURCE_LABEL[a.source] && (
                    <>
                      <span>·</span>
                      <span>{SOURCE_LABEL[a.source]}</span>
                    </>
                  )}
                  {a.is_internal && (
                    <span className="rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 px-2 py-0.5 font-medium">
                      Internal
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => download(a)}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 px-2 py-1 shrink-0"
              >
                Download
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => remove(a)}
                  aria-label={`Delete ${a.filename}`}
                  className="text-xs text-gray-400 hover:text-red-600 dark:hover:text-red-400 px-1.5 py-1 shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canUpload && (
        <div
          ref={zoneRef}
          onPaste={onPaste}
          tabIndex={0}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer?.files?.length) upload(e.dataTransfer.files);
          }}
          className={`rounded-lg border-2 border-dashed px-4 py-4 text-center transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand/30 ${
            dragging
              ? "border-blue-400 bg-blue-50/60 dark:bg-blue-500/10"
              : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
          }`}
          onClick={() => fileInput.current?.click()}
        >
          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) upload(e.target.files);
              // Cleared so re-selecting the same file fires change again.
              e.target.value = "";
            }}
          />
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-blue-600 animate-spin" />
                Uploading…
              </span>
            ) : (
              <>
                <span className="font-medium text-blue-700 dark:text-blue-400">Choose a file</span>
                , drag one here, or paste a screenshot
              </>
            )}
          </div>
          <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
            Up to {MAX_MB} MB each
          </div>
        </div>
      )}

      {!canUpload && items.length === 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500">No attachments.</p>
      )}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-2" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
